import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { UserContext } from '@providers/userContextProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { calculateDurationSeconds } from '@utils/analytics/eventMetrics';
import { useContext, useEffect, useRef, useState } from 'react';
import type {
  CreateModelPortfolioProps,
  ModelPortfolioData,
} from './createModelPortfolio.types';
import { CreateModelPortfolioVisual } from './createModelPortfolio.visual';

export type { ModelPortfolioGroupBy } from './modelPortfolioGroupBy';
export {
  DEFAULT_MODEL_PORTFOLIO_GROUP_BY,
  MODEL_PORTFOLIO_GROUP_BY,
} from './modelPortfolioGroupBy';

export const CreateModelPortfolio = ({
  options,
  onNext,
  stepLoading,
  workflowCompletion,
  onBack,
}: CreateModelPortfolioProps) => {
  const { activeOrganization } = useContext(UserContext);
  const { sendAnalytic } = useContext(AnalyticsContext);
  const {
    workflowData,
    setWorkflowData,
    updateWorkflowContext,
    navigateToSegment,
  } = useContext(WorkflowContext);
  const stepStartTime = useRef<Date | null>(null);

  const [modelPortfolioData, setModelPortfolioData] =
    useState<ModelPortfolioData>({
      organizationId: activeOrganization?.id || '',
      creationType: workflowData.modelPortfolioDetails?.creationType,
      portfolioTemplate: workflowData.modelPortfolioDetails?.portfolioTemplate,
    });

  // Track step start time when component mounts
  useEffect(() => {
    stepStartTime.current = new Date();
  }, []);

  const handleContinue = async (portfolio: ModelPortfolioData) => {
    try {
      const updatedWorkflowData = {
        ...workflowData,
        modelPortfolioDetails: {
          ...workflowData.modelPortfolioDetails,
          ...portfolio,
        },
      };

      await setWorkflowData(updatedWorkflowData);

      if (updateWorkflowContext) {
        await updateWorkflowContext();
      }

      // Calculate duration and send analytics before proceeding to next step
      const stepEndTime = new Date();
      const formDuration = calculateDurationSeconds(stepStartTime.current, {
        endTime: stepEndTime,
      });

      sendAnalytic(ovAnalyticsEvents.modelPortfolioWorkflowStep1Complete, {
        stepNumber: 1,
        stepName: 'CREATE_MODEL_PORTFOLIO',
        formDuration,
        timestamp: stepEndTime.toISOString(),
        workflowCompletionId: workflowCompletion?.id,
      });

      onNext();
    } catch {
      // Error is handled in the parent component
    }
  };

  const handleUpdateModelPortfolio = (updates: Partial<ModelPortfolioData>) => {
    setModelPortfolioData((prev) => {
      const updatedData = {
        ...prev,
        ...updates,
      };

      if (updates.portfolioTemplate) {
        const updatedWorkflowData = {
          ...workflowData,
          modelPortfolioDetails: {
            ...workflowData.modelPortfolioDetails,
            portfolioTemplate: updates.portfolioTemplate,
            templateCreationInProgress: true,
          },
        };
        setWorkflowData(updatedWorkflowData);
      }

      return updatedData;
    });
  };

  return (
    <CreateModelPortfolioVisual
      options={options}
      modelPortfolioData={modelPortfolioData}
      updateModelPortfolio={handleUpdateModelPortfolio}
      continueFunc={handleContinue}
      loading={stepLoading}
      workflowCompletion={workflowCompletion}
      workflowData={workflowData}
      setWorkflowData={setWorkflowData}
      onBack={onBack}
      navigateToSegment={navigateToSegment || (() => {})}
    />
  );
};
